import React, { useEffect, useState } from "react";
import { Box, Toolbar, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { getTransactions } from "../../services/transactionService";
import TableRowsLoader from "../../components/common/Loader/tableLoader";
import TransactionTable from "../../components/clientTransactionTable/TransactionTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  setTransactionsDetailsState,
  updateTimeframe,
} from "../../redux/features/clientTransactionSlice";
import "./styles.scss";
import { getTransactionsFromData } from "../../utils/utils";
import TableFilters from "../../components/clientTransactionTable/TableFilters";
import { AccountType, TransactionDetailsType } from "../../types/genericType";
import { getAccounts } from "../../services/accountService";
import { DropdownOptions } from "../../components/common/Dropdown/Dropdown";

const ClientTransactions = () => {
  const { transactionDetails, startFilterDate, endFilterDate } = useSelector(
    (state: RootState) => state.clientTransactionsState
  );
  let [searchParams, setSearchParams] = useSearchParams();
  const account = searchParams?.get("account");
  const client = searchParams?.get("client");
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const [transactionResponse, setTransactionResponse] = useState<TransactionDetailsType[]>(transactionDetails);
  const [accountOptions, setAccountOptions] = useState<DropdownOptions[]>([
    { value: "0", label: "All Accounts" },
  ]);
  const [selectedAccount, setSelectedAccount] = useState<string>(account || "0");

  const handleAccountChange = (val: string) => {
    setSelectedAccount(val);
    const nextParams = new URLSearchParams(searchParams);
    if (!val || val === "0") {
      nextParams.delete("account");
    } else {
      nextParams.set("account", val);
    }
    setSearchParams(nextParams);
  };

  const getFilteredTransactions = (transactions: TransactionDetailsType[]) => {
    if(transactions?.length > 0 && (!!client || !!account)) {
      return transactions.filter((transaction) => {
        if(client && account) {
          return transaction.clientId === parseInt(client) && transaction.account === parseInt(account);
        } else if(client) {
          return transaction.clientId === parseInt(client);
        } else if(account) {
          return transaction.account === parseInt(account);
        }
        return false;
      });
    }
    return null;
  };

  useEffect(() => {
    getAccounts()
      ?.then((res: AccountType[]) => {
        const opts: DropdownOptions[] = [{ value: "0", label: "All Accounts" }];
        res?.forEach((acc) => {
          if (acc?.isActive) {
            opts.push({
              value: acc.accountNumber?.toString(),
              label: `${acc.name} - ${acc.accountNumber}`,
            });
          }
        });
        setAccountOptions(opts);
      })
      .catch((err: any) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    setSelectedAccount(account || "0");
  }, [account]);

  const getAllTransactions = () => {
    setIsLoading(true);
    getTransactions(startFilterDate, endFilterDate)
      .then((res) => {
        const transactions = getTransactionsFromData(res);
        setTransactionResponse(transactions);
        const filteredTransactions = getFilteredTransactions(transactions) || transactions;
        dispatch(setTransactionsDetailsState(filteredTransactions));
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
    dispatch(setTransactionsDetailsState(transactionResponse));
  };

  useEffect(() => {
    if (transactionResponse?.length > 0) {
      const filteredTransactions = getFilteredTransactions(transactionResponse);
      dispatch(setTransactionsDetailsState(filteredTransactions || transactionResponse));
    }
  }, [client, account, transactionResponse]);

  useEffect(() => {
    return () => {
      dispatch(updateTimeframe("0"));
    };
  }, []);

  useEffect(() => {
    if (startFilterDate && endFilterDate) {
      getAllTransactions();
    }
  }, [startFilterDate, endFilterDate]);

  return (
    <>
      <Toolbar
        sx={{
          justifyContent: "space-between",
          margin: "0px 15px",
        }}
      >
        <Typography className="transactions-history-title">
          Transactions
        </Typography>
      </Toolbar>
      <Box sx={{ margin: "20px 40px" }}>
        <div className="data-table">
          <TableFilters
            showClearFilters={!!client || !!account}
            clearFilters={clearFilters}
            accountOptions={accountOptions}
            account={selectedAccount}
            setAccount={handleAccountChange}
          />
          {isLoading ? (
            <TableRowsLoader rowsNum={10} />
          ) : transactionDetails.length > 0 ? (
            <TransactionTable transactionData={transactionDetails} />
          ) : (
            <Typography>No transaction records found</Typography>
          )}
        </div>
      </Box>
    </>
  );
};

export default ClientTransactions;
